// src/pages/LoginPage.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiOutlineMail, HiOutlineLockClosed, HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";
import { useUser } from "./../Home/UserContext";
import { loginUser } from "./../../services/userServices";
import { getSettings } from "./../../services/settingsServices";
import useScrollToTop from "./../../hooks/useScollToTop";

/* ========= Helpers identiques à ErrorPage ========= */
function toAbsolute(u = "") {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${window.location.origin}${u}`;
  return u;
}

function pickIndex(n, exclude = -1) {
  if (n <= 1) return 0;
  let idx = Math.floor(Math.random() * n);
  if (idx === exclude) idx = (idx + 1) % n;
  return idx;
}
/* ================================================ */

export default function LoginPage() {
  useScrollToTop();

  const [bgUrl, setBgUrl] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const { updateUser } = useUser();
  const navigate = useNavigate();

  // ✅ Même logique de fond que ErrorPage
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getSettings();
        const s = Array.isArray(data) ? data[0] : data;

        const raw = [s?.photoURL1, s?.photoURL2, s?.photoURL3].filter(
          (u) => typeof u === "string" && u.trim().length > 0
        );
        const unique = Array.from(new Set(raw));
        const candidates = unique.map(toAbsolute);

        if (!mounted) return;

        if (candidates.length > 0) {
          const prev = Number(sessionStorage.getItem("loginBgIdx"));
          const idx = pickIndex(
            candidates.length,
            Number.isNaN(prev) ? -1 : prev
          );
          sessionStorage.setItem("loginBgIdx", String(idx));
          setBgUrl(candidates[idx]);
        } else {
          setBgUrl(null);
        }
      } catch (e) {
        console.error("LoginPage getSettings:", e);
        if (mounted) setBgUrl(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    try {
      const data = await loginUser(email, password);

      if (!data?.userInfo || !data?.token) {
        setErrorMessage(
          "Email ou mot de passe incorrect. Veuillez réessayer."
        );
        setLoading(false);
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("userInfo", JSON.stringify(data.userInfo));
      updateUser(data.userInfo);

      navigate("/"); // Redirection vers l’accueil ou le dashboard
    } catch (error) {
      console.error("login error:", error);
      setErrorMessage(
        "Impossible de se connecter pour le moment. Veuillez réessayer plus tard."
      );
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen">
      {/* Fond identique à ErrorPage */}
      <div
        className={`absolute inset-0 ${
          bgUrl
            ? "bg-cover bg-center bg-no-repeat"
            : "bg-gradient-to-b from-slate-900 to-slate-700"
        }`}
        style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
        aria-hidden="true"
      />
      {/* Voile pour lisibilité */}
      <div className="absolute inset-0 bg-slate-900/50 dark:bg-slate-950/60" aria-hidden="true" />

      {/* Contenu */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Petit header / badge appli */}
          <div className="mb-4 text-center text-slate-100">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-xs uppercase tracking-wide border border-white/20 mb-3">
              Espace administration
            </span>
            <h1 className="text-3xl font-bold drop-shadow-sm">
              Connexion à MonAppliMairie
            </h1>
            <p className="mt-1 text-sm text-slate-200/90">
              Accédez au tableau de bord de votre collectivité.
            </p>
          </div>

          {/* Carte de login */}
          <div className="bg-white/80 dark:bg-slate-900/90 backdrop-blur-md rounded-2xl shadow-xl border border-white/40 dark:border-slate-800/70 p-6 md:p-7">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email */}
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Email
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
                    <HiOutlineMail className="h-5 w-5" />
                  </span>
                  <input
                    type="email"
                    placeholder="nom@mairie.fr"
                    className="mt-0 block w-full pl-10 pr-3 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                               focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                               dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* Mot de passe */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Mot de passe
                  </label>
                </div>
                <div className="relative">
                  <span className="absolute inset-y-0 left-3 flex items-center text-slate-400 pointer-events-none">
                    <HiOutlineLockClosed className="h-5 w-5" />
                  </span>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Votre mot de passe"
                    className="mt-0 block w-full pl-10 pr-10 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                               focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                               dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute inset-y-0 right-2 flex items-center px-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  >
                    {showPassword ? (
                      <HiOutlineEyeOff className="h-5 w-5" />
                    ) : (
                      <HiOutlineEye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Erreur */}
              {errorMessage && (
                <div
                  className="rounded-xl border border-red-200 bg-red-50/90 text-red-800 text-sm px-3 py-2 flex items-start gap-2 dark:bg-red-900/40 dark:border-red-800 dark:text-red-100"
                  role="alert"
                >
                  <span className="mt-0.5">⚠️</span>
                  <span>{errorMessage}</span>
                </div>
              )}

              {/* Bouton */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full px-4 py-2.5 rounded-xl shadow-sm border text-sm font-medium flex items-center justify-center gap-2
                 transition-transform duration-150
                 ${
                   loading
                     ? "bg-slate-400 text-white cursor-not-allowed border-slate-300"
                     : "bg-primary text-white border-primary hover:bg-white hover:text-primary transform hover:-translate-y-0.5 hover:shadow-md"
                 }`}
              >
                {loading && (
                  <span className="inline-flex h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />
                )}
                <span>{loading ? "Connexion..." : "Se connecter"}</span>
              </button>

              {/* Liens secondaires */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm mt-1.5">
                <Link
                  to="/forgot-password"
                  className="text-primary hover:underline"
                >
                  Mot de passe oublié ?
                </Link>

                <div className="text-slate-600 dark:text-slate-300 text-xs sm:text-sm">
                  Accès réservé aux administrateurs et utilisateurs invités.
                </div>
              </div>

              {/* Lien création compte si tu souhaites garder l’URL */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 text-center text-xs text-slate-500 dark:text-slate-400">
                Vous avez reçu une invitation ? Créez votre accès depuis le lien transmis,
                puis connectez-vous ici.
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
